import type { ChatMessage, LobbyState } from '../lobbyStore.types';

/** Generate a unique message ID using crypto.randomUUID (collision-free) */
export function msgId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

/**
 * Route an NPC message through the reveal queue if the room is active,
 * or add it directly if it's a background room (no animation needed).
 * If the room doesn't exist yet, routes through addMessage which buffers
 * into pendingPartyMessages for party- rooms (non-party rooms are silently dropped).
 */
export function addOrEnqueueMessage(state: LobbyState, roomId: string, message: ChatMessage): void {
  const roomExists = state.chatRooms.some((r) => r.id === roomId);
  if (roomExists && roomId === state.activeChatRoomId) {
    state.enqueueRevealMessage(roomId, message);
  } else {
    state.addMessage(roomId, message);
  }
}

/** Maximum completed stream IDs to retain for deduplication */
const MAX_COMPLETED_STREAMS = 100;

/**
 * Prune and add a stream ID to the completed streams map.
 * Keeps only the last MAX_COMPLETED_STREAMS entries to prevent unbounded growth.
 */
export function addCompletedStreamId(
  completedStreamIds: Record<string, true>,
  streamId: string
): Record<string, true> {
  const keys = Object.keys(completedStreamIds);
  const pruned: Record<string, true> =
    keys.length >= MAX_COMPLETED_STREAMS
      ? Object.fromEntries(keys.slice(-(MAX_COMPLETED_STREAMS - 1)).map((k) => [k, true as const]))
      : { ...completedStreamIds };
  pruned[streamId] = true;
  return pruned;
}
